import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react'
import { load, save, clearAll } from '../lib/storage'
import { uid } from '../lib/id'
import { seedState } from './seed'
import { detectLang } from '../i18n'

/**
 * Increment this whenever the seed *content* changes (wiki pages, tasks,
 * applications, subjects).
 *
 * For tasks, events, applications and subjects the rule is conservative: they
 * refresh from a newer seed ONLY while the install is still pristine. Once she
 * has edited anything, her stored data always wins (see `keepStoredContent`).
 *
 * Wiki pages are the exception — they are reference material rather than her
 * own work, so they are merged page by page and a bump reaches even an install
 * that has been used. See `mergeWiki`. Sessions, notes, settings and quickNote
 * are always preserved regardless.
 */
const SEED_VERSION = 4
import type {
  AppState,
  Application,
  CalEvent,
  Note,
  Requirement,
  Settings,
  StudySession,
  Subject,
  Task,
  Track,
  WikiPage,
} from './types'

/* Each slice persists independently — see lib/storage.ts for why. */
const SLICES = [
  'settings',
  'events',
  'subjects',
  'tasks',
  'sessions',
  'applications',
  'tracks',
  'wiki',
  'notes',
  'quickNote',
] as const
type Slice = (typeof SLICES)[number]

/**
 * Wiki pages are reference material, not Ala's own writing, so they are merged
 * per page rather than kept or replaced wholesale.
 *
 * The blanket rule below (`keepStoredContent`) is right for tasks, events and
 * applications — those are hers, and a seed bump must never rewrite them. It is
 * wrong for the study guide: one edited task used to freeze the wiki forever,
 * so corrections and new modules could never reach an install that had been
 * used. This keeps only the pages she actually touched:
 *
 *   - a seed page she edited        → her copy wins (matched by id)
 *   - a seed page she deleted       → stays deleted (matched by title)
 *   - every other seed page         → refreshed from the current seed
 *   - a page she wrote herself      → kept as-is
 */
function mergeWiki(seedPages: WikiPage[]): WikiPage[] {
  const stored = load<WikiPage[]>('wiki', [])
  if (!stored.length) return seedPages

  const edited = new Set(load<string[]>('wikiEdited', []))
  const removed = new Set(load<string[]>('wikiRemoved', []))
  const storedById = new Map(stored.map((p) => [p.id, p]))
  const storedByTitle = new Map(stored.map((p) => [p.title, p]))
  const seedIds = new Set(seedPages.map((p) => p.id))
  const seedTitles = new Set(seedPages.map((p) => p.title))

  /* Seed ids were random before they were derived from the title, so an install
     created by an older build holds the same pages under ids this seed will
     never produce. Fall back to matching on title, or every one of them would
     be mistaken for a page Ala wrote and kept alongside its own replacement. */
  const storedCopyOf = (p: WikiPage) => storedById.get(p.id) ?? storedByTitle.get(p.title)

  return [
    // Deletions are recorded by title, not id: the page is gone from storage by
    // definition, so there is no stored copy left to map an old id through.
    ...seedPages
      .filter((p) => !removed.has(p.title))
      .map((p) => {
        const mine = storedCopyOf(p)
        return mine && edited.has(mine.id) ? mine : p
      }),
    // Pages she wrote herself: matched by neither id nor title.
    ...stored.filter((p) => !seedIds.has(p.id) && !seedTitles.has(p.title)),
  ]
}

/**
 * Append to one of the two bookkeeping lists, if it isn't there already.
 * `wikiEdited` holds page ids; `wikiRemoved` holds titles, because a deleted
 * page leaves no stored record to match an id against later.
 */
function markWiki(key: 'wikiEdited' | 'wikiRemoved', value: string): void {
  const list = load<string[]>(key, [])
  if (!list.includes(value)) save(key, [...list, value])
}

function loadState(): AppState {
  const seeded = load<boolean>('seeded', false)
  const storedSeedVersion = load<number>('seedVersion', 0)
  const stored = load<Partial<Settings>>('settings', {})
  const base = seedState(stored.lang ?? detectLang(), stored.targetIntake ?? 'aug2027')

  // First run — return seed as-is; the seededOnce effect will persist it.
  if (!seeded) return base

  // Content slices (wiki, tasks, applications, events, subjects) refresh from a
  // newer seed only while the install is still pristine. The moment Ala edits
  // ANY of her own content (`touched`), her stored data wins for good — a seed
  // bump must never silently delete work she has done. This is deliberately
  // coarse: `touched` is a single global flag, so one edit freezes every content
  // slice. That errs toward keeping her data, which is the whole point.
  const touched = load<boolean>('touched', false)
  const keepStoredContent = storedSeedVersion >= SEED_VERSION || touched

  const fullState: AppState = {
    ...base,
    settings: { ...base.settings, ...load<Partial<Settings>>('settings', {}) },
    // Object URLs from uploaded files die with the session, so drop them on load.
    tracks: load<Track[]>('tracks', base.tracks).map(({ url: _url, ...t }) => t),
    // User-generated slices always win.
    sessions: load('sessions', base.sessions),
    notes: load('notes', base.notes),
    quickNote: load('quickNote', base.quickNote),
    // Content slices: keep stored data if the seed is current OR she has edited.
    events: keepStoredContent ? load('events', base.events) : base.events,
    subjects: keepStoredContent ? load('subjects', base.subjects) : base.subjects,
    tasks: keepStoredContent ? load('tasks', base.tasks) : base.tasks,
    applications: keepStoredContent ? load('applications', base.applications) : base.applications,
    wiki: mergeWiki(base.wiki),
  }

  return fullState
}

interface Store {
  state: AppState
  /** True when a write to localStorage failed (quota / private browsing). */
  storageBlocked: boolean

  /** False once the user has edited any content — see `touch()`. */
  pristine: boolean
  setSettings: (patch: Partial<Settings>) => void

  addEvent: (e: Omit<CalEvent, 'id'>) => void
  updateEvent: (id: string, patch: Partial<CalEvent>) => void
  removeEvent: (id: string) => void

  addTask: (t: Pick<Task, 'title'> & Partial<Task>) => void
  updateTask: (id: string, patch: Partial<Task>) => void
  removeTask: (id: string) => void
  toggleTask: (id: string) => void

  addSubject: (s: Omit<Subject, 'id'>) => void
  updateSubject: (id: string, patch: Partial<Subject>) => void
  removeSubject: (id: string) => void

  logSession: (s: Omit<StudySession, 'id' | 'at'> & { at?: number }) => void
  removeSession: (id: string) => void

  addApplication: (a: Omit<Application, 'id' | 'requirements'> & { requirements?: Requirement[] }) => void
  updateApplication: (id: string, patch: Partial<Application>) => void
  removeApplication: (id: string) => void
  toggleRequirement: (appId: string, reqId: string) => void
  addRequirement: (appId: string, label: string) => void
  removeRequirement: (appId: string, reqId: string) => void

  addTrack: (t: Omit<Track, 'id'>) => string
  removeTrack: (id: string) => void

  addWiki: (page: Pick<WikiPage, 'title'> & Partial<WikiPage>) => string
  updateWiki: (id: string, patch: Partial<WikiPage>) => void
  removeWiki: (id: string) => void

  setQuickNote: (v: string) => void
  pinNote: () => void
  removeNote: (id: string) => void

  exportJSON: () => string
  importJSON: (raw: string) => { ok: boolean; error?: string }
  resetAll: () => void
}

const Ctx = createContext<Store | null>(null)

export function AppStoreProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AppState>(loadState)
  const [storageBlocked, setBlocked] = useState(false)
  const [pristine, setPristine] = useState(() => !load<boolean>('touched', false))

  /* On the very first run, write the seed out so the next load is a real
     restore rather than a re-seed. On subsequent runs where the seed version
     has been bumped, re-persist the content slices that were re-seeded. */
  const seededOnce = useRef(false)
  useEffect(() => {
    if (seededOnce.current) return
    seededOnce.current = true
    const storedSeedVersion = load<number>('seedVersion', 0)
    if (!load<boolean>('seeded', false)) {
      // First run: persist everything.
      SLICES.forEach((k) => save(k, state[k]))
      save('seeded', true)
      save('seedVersion', SEED_VERSION)
    } else if (storedSeedVersion < SEED_VERSION) {
      // Seed content changed: persist the content slices as loadState resolved
      // them — the fresh seed for a pristine install, or her own preserved data
      // if she has edited (loadState keeps stored content once `touched`). Then
      // record the new version so this reconciliation runs only once.
      const contentSlices = ['events', 'subjects', 'tasks', 'applications', 'wiki'] as const
      contentSlices.forEach((k) => save(k, state[k]))
      save('seedVersion', SEED_VERSION)
    }
    // Intentionally runs once against the initial state.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  /** Writes one slice, flagging the UI if storage is unavailable. */
  const persist = useCallback((slice: Slice, value: unknown) => {
    if (!save(slice, value)) setBlocked(true)
  }, [])

  /* Any edit to *content* (not settings) means the seed is no longer safely
     replaceable — switching language or intake must then keep what was written
     rather than regenerating it. */
  const touch = useCallback(() => {
    setPristine((was) => {
      if (was) save('touched', true)
      return false
    })
  }, [])

  const api = useMemo<Store>(() => {
    /* Small helpers to keep the action bodies to one line each. */
    const listOp =
      <K extends 'events' | 'tasks' | 'subjects' | 'sessions' | 'applications' | 'tracks' | 'wiki' | 'notes'>(
        key: K,
      ) =>
      (fn: (list: AppState[K]) => AppState[K]) =>
        setState((prev) => {
          const next = { ...prev, [key]: fn(prev[key]) } as AppState
          persist(key, next[key])
          touch()
          return next
        })

    const events = listOp('events')
    const tasks = listOp('tasks')
    const subjects = listOp('subjects')
    const sessions = listOp('sessions')
    const apps = listOp('applications')
    const tracks = listOp('tracks')
    const wiki = listOp('wiki')
    const notes = listOp('notes')

    const patchApp = (id: string, fn: (a: Application) => Application) =>
      apps((list) => list.map((a) => (a.id === id ? fn(a) : a)))

    return {
      state,
      storageBlocked,
      pristine,

      setSettings: (patch) =>
        setState((prev) => {
          const settings = { ...prev.settings, ...patch }
          const langChanged = patch.lang !== undefined && patch.lang !== prev.settings.lang
          const intakeChanged =
            patch.targetIntake !== undefined && patch.targetIntake !== prev.settings.targetIntake

          /* Language switch: always re-seed content slices so wiki pages,
             tasks, events and applications appear in the chosen language.
             User-generated slices (sessions, notes, quickNote) are kept. */
          if (langChanged) {
            const fresh = seedState(settings.lang, settings.targetIntake)
            const next: AppState = {
              ...fresh,
              settings,
              sessions: prev.sessions,
              notes: prev.notes,
              quickNote: prev.quickNote,
            }
            SLICES.forEach((k) => persist(k, next[k]))
            return next
          }

          /* Intake change on an untouched app regenerates the timeline. Once
             the user has edited anything, their dates are kept. */
          if (intakeChanged && pristine) {
            const fresh = seedState(settings.lang, settings.targetIntake)
            const next: AppState = { ...fresh, settings }
            SLICES.forEach((k) => persist(k, next[k]))
            return next
          }

          persist('settings', settings)
          return { ...prev, settings }
        }),

      /* -------------------------------------------------------- calendar */
      addEvent: (e) => events((l) => [...l, { ...e, id: uid('e') }]),
      updateEvent: (id, patch) => events((l) => l.map((e) => (e.id === id ? { ...e, ...patch } : e))),
      removeEvent: (id) => events((l) => l.filter((e) => e.id !== id)),

      /* ----------------------------------------------------------- tasks */
      addTask: (t) =>
        tasks((l) => [
          { id: uid('t'), done: false, priority: 0, createdAt: Date.now(), ...t } as Task,
          ...l,
        ]),
      updateTask: (id, patch) => tasks((l) => l.map((t) => (t.id === id ? { ...t, ...patch } : t))),
      removeTask: (id) => tasks((l) => l.filter((t) => t.id !== id)),
      toggleTask: (id) => tasks((l) => l.map((t) => (t.id === id ? { ...t, done: !t.done } : t))),

      /* -------------------------------------------------------- subjects */
      addSubject: (s) => subjects((l) => [...l, { ...s, id: uid('sub') }]),
      updateSubject: (id, patch) =>
        subjects((l) => l.map((s) => (s.id === id ? { ...s, ...patch } : s))),
      removeSubject: (id) => {
        subjects((l) => l.filter((s) => s.id !== id))
        // Orphaned references would render as a blank hue chip, so clear them.
        tasks((l) => l.map((t) => (t.subjectId === id ? { ...t, subjectId: undefined } : t)))
        sessions((l) => l.map((s) => (s.subjectId === id ? { ...s, subjectId: undefined } : s)))
      },

      /* -------------------------------------------------------- sessions */
      logSession: (s) => sessions((l) => [{ ...s, at: s.at ?? Date.now(), id: uid('s') }, ...l]),
      removeSession: (id) => sessions((l) => l.filter((s) => s.id !== id)),

      /* ---------------------------------------------------- applications */
      addApplication: (a) =>
        apps((l) => [...l, { requirements: [], ...a, id: uid('app') } as Application]),
      updateApplication: (id, patch) => patchApp(id, (a) => ({ ...a, ...patch })),
      removeApplication: (id) => {
        apps((l) => l.filter((a) => a.id !== id))
        events((l) => l.filter((e) => e.applicationId !== id))
      },
      toggleRequirement: (appId, reqId) =>
        patchApp(appId, (a) => ({
          ...a,
          requirements: a.requirements.map((r) => (r.id === reqId ? { ...r, done: !r.done } : r)),
        })),
      addRequirement: (appId, label) =>
        patchApp(appId, (a) => ({
          ...a,
          requirements: [...a.requirements, { id: uid('r'), label, done: false }],
        })),
      removeRequirement: (appId, reqId) =>
        patchApp(appId, (a) => ({
          ...a,
          requirements: a.requirements.filter((r) => r.id !== reqId),
        })),

      /* --------------------------------------------------------- library */
      addTrack: (t) => {
        const id = uid('tr')
        tracks((l) => [{ ...t, id }, ...l])
        return id
      },
      removeTrack: (id) => tracks((l) => l.filter((t) => t.id !== id)),

      addWiki: (page) => {
        const id = uid('w')
        wiki((l) => [
          { id, tag: 'note', body: `# ${page.title}\n\n`, updatedAt: Date.now(), ...page } as WikiPage,
          ...l,
        ])
        return id
      },
      /* Note which seed pages she has made her own, so a later seed bump
         refreshes the untouched guide without overwriting her work. */
      updateWiki: (id, patch) => {
        markWiki('wikiEdited', id)
        wiki((l) => l.map((p) => (p.id === id ? { ...p, ...patch, updatedAt: Date.now() } : p)))
      },
      removeWiki: (id) =>
        wiki((l) => {
          const page = l.find((p) => p.id === id)
          if (page) markWiki('wikiRemoved', page.title)
          return l.filter((p) => p.id !== id)
        }),

      setQuickNote: (v) =>
        setState((prev) => {
          persist('quickNote', v)
          touch()
          return { ...prev, quickNote: v }
        }),

      pinNote: () =>
        setState((prev) => {
          const text = prev.quickNote.trim()
          if (!text) return prev
          const hues = ['amber', 'sage', 'iris', 'rose', 'teal'] as const
          const note: Note = {
            id: uid('n'),
            text,
            hue: hues[prev.notes.length % hues.length],
            createdAt: Date.now(),
          }
          const next = { ...prev, notes: [note, ...prev.notes], quickNote: '' }
          persist('notes', next.notes)
          persist('quickNote', '')
          touch()
          return next
        }),

      removeNote: (id) => notes((l) => l.filter((n) => n.id !== id)),

      /* ------------------------------------------------------------ data */
      exportJSON: () => JSON.stringify(state, null, 2),

      importJSON: (raw) => {
        try {
          const data = JSON.parse(raw) as Partial<AppState>
          if (typeof data !== 'object' || data === null) throw new Error('not an object')
          if (!Array.isArray(data.events) || !Array.isArray(data.tasks))
            throw new Error('missing expected fields — is this an ALa export?')
          const merged = { ...seedState(state.settings.lang), ...data, version: 1 } as AppState
          setState(merged)
          SLICES.forEach((k) => persist(k, merged[k]))
          touch()
          return { ok: true }
        } catch (e) {
          return { ok: false, error: e instanceof Error ? e.message : 'could not read that file' }
        }
      },

      resetAll: () => {
        const { lang, targetIntake } = state.settings
        clearAll()
        const fresh = seedState(lang, targetIntake)
        setState(fresh)
        save('seeded', true)
        SLICES.forEach((k) => save(k, fresh[k]))
        setPristine(true)
      },
    }
  }, [state, storageBlocked, pristine, persist, touch])

  return <Ctx.Provider value={api}>{children}</Ctx.Provider>
}

export function useStore(): Store {
  const ctx = useContext(Ctx)
  if (!ctx) throw new Error('useStore must be used inside <AppStoreProvider>')
  return ctx
}

/** Convenience: the settings slice on its own. */
export function useSettings() {
  const { state, setSettings } = useStore()
  return [state.settings, setSettings] as const
}
